// Shared UI kit. Every page builds from these; don't hand-roll buttons, inputs, cards or tables.
import {
  createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from "react";
import { Icon, type IconName } from "./icons.tsx";

export { Icon, type IconName };

export const cn = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

// ── Buttons ──

type Variant = "primary" | "secondary" | "ghost" | "danger";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-gradient-to-b from-accent-hover to-accent text-accent-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.25),0_8px_24px_-8px_rgb(255_61_79/0.7)] hover:brightness-110 active:brightness-95",
  secondary: "bg-raised/80 text-fg border border-line shadow-[inset_0_1px_0_rgb(255_255_255/0.05)] hover:bg-hover hover:border-line-strong",
  ghost: "text-muted hover:text-fg hover:bg-hover",
  danger: "bg-bad/10 text-bad border border-bad/30 hover:bg-bad/20",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, disabled, className, children, type = "button", ...rest }, ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap transition-[filter,background-color,border-color,color,transform] active:translate-y-px disabled:opacity-50 disabled:pointer-events-none",
        size === "sm" && "h-8 px-3 text-xs",
        size === "md" && "h-9 px-4 text-sm",
        size === "lg" && "h-11 px-5 text-sm",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : icon && <Icon name={icon} className="size-4 shrink-0" />}
      {children}
    </button>
  );
});

export function IconButton({ icon, label, className, ...rest }: Omit<ButtonProps, "children" | "icon"> & { icon: IconName; label: string }) {
  return (
    <button type="button" aria-label={label} title={label}
      className={cn("inline-flex size-8 items-center justify-center rounded-lg text-muted hover:text-fg hover:bg-hover transition-colors disabled:opacity-50", className)} {...rest}>
      <Icon name={icon} className="size-4" />
    </button>
  );
}

// ── Form controls ──

const control = "w-full rounded-lg border border-line bg-bg/60 px-3 text-sm shadow-[inset_0_1px_2px_rgb(0_0_0/0.4)] text-fg placeholder:text-faint transition-colors hover:border-line-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(control, "h-9", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(control, "min-h-24 py-2 leading-relaxed", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return <select ref={ref} className={cn(control, "h-9 pr-8 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%238b8b94%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>')] bg-[length:16px] bg-[right_0.6rem_center] bg-no-repeat", className)} {...rest}>{children}</select>;
});

export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={cn("inline-flex items-center gap-2 text-sm cursor-pointer select-none", disabled && "opacity-50 pointer-events-none")}>
      <input type="checkbox" className="size-4 rounded border-line accent-[var(--color-accent)]" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      {label}
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cn("inline-flex items-center gap-3 text-sm cursor-pointer select-none", disabled && "opacity-50 pointer-events-none")}>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={cn("relative h-5 w-9 shrink-0 rounded-full transition-colors", checked ? "bg-accent shadow-[0_0_12px_-2px_var(--color-accent)]" : "bg-line-strong")}>
        <span className={cn("absolute left-0 top-0.5 size-4 rounded-full bg-white shadow transition-transform", checked ? "translate-x-[18px]" : "translate-x-0.5")} />
      </button>
      {label}
    </label>
  );
}

export function Field({ label, hint, error, children, className }: { label?: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string }) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      {label && <span className="text-xs font-medium text-muted">{label}</span>}
      {children}
      {error ? <span className="text-xs text-bad">{error}</span> : hint ? <span className="text-xs text-faint">{hint}</span> : null}
    </label>
  );
}

/** Inline error banner for forms and failed loads. */
export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-bad/30 bg-bad/10 px-3 py-2 text-sm text-bad">
      <Icon name="alert" className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// ── Surfaces ──

export function Card({ title, description, actions, children, className, padded = true, hud }: {
  title?: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string; padded?: boolean;
  /** Corner-bracket frame. */
  hud?: boolean;
}) {
  return (
    <section className={cn("pane", hud && "hud", className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="font-display text-[15px] font-semibold tracking-wide">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? "p-5" : undefined}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, description, actions, kicker }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; kicker?: string }) {
  const path = typeof window !== "undefined" ? window.location.pathname.split("/").filter(Boolean) : [];
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4 animate-rise">
      <div>
        <div className="kicker mb-2 flex items-center gap-2">
          <span className="size-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />
          {kicker ?? `// ${path.join(" / ") || "home"}`}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

type Tone = "neutral" | "ok" | "warn" | "bad" | "info" | "accent";
const TONES: Record<Tone, string> = {
  neutral: "bg-raised text-muted border-line",
  ok: "bg-ok/10 text-ok border-ok/25",
  warn: "bg-warn/10 text-warn border-warn/25",
  bad: "bg-bad/10 text-bad border-bad/25",
  info: "bg-info/10 text-info border-info/25",
  accent: "bg-accent-soft text-accent border-accent/25",
};

export function Badge({ tone = "neutral", dot, children, className }: { tone?: Tone; dot?: boolean; children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase leading-4 tracking-wider whitespace-nowrap", TONES[tone], className)}>
      {dot && <span className="size-1.5 rounded-full bg-current shadow-[0_0_8px_currentColor]" />}
      {children}
    </span>
  );
}

/** onMouseMove handler that feeds the .spotlight hover glow. */
export function spot(e: React.MouseEvent<HTMLElement>) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
}

export function Stat({ label, value, hint, icon, tone }: { label: string; value: ReactNode; hint?: ReactNode; icon?: IconName; tone?: Tone }) {
  return (
    <div className="pane hud spotlight p-4" onMouseMove={spot}>
      <div className="flex items-center justify-between">
        <span className="kicker">{label}</span>
        {icon && <Icon name={icon} className={cn("size-4", tone === "bad" ? "text-bad" : tone === "warn" ? "text-warn" : tone === "ok" ? "text-ok" : "text-faint")} />}
      </div>
      <div className="mt-3 font-display text-3xl font-semibold tabular-nums tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </div>
  );
}

// ── Tables ──

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  );
}
export const THead = ({ children }: { children: ReactNode }) => <thead className="border-b border-line font-mono text-[10px] uppercase tracking-[0.14em] text-faint">{children}</thead>;
export const TBody = ({ children }: { children: ReactNode }) => <tbody className="divide-y divide-line">{children}</tbody>;
export const Th = ({ children, className }: { children?: ReactNode; className?: string }) => <th className={cn("px-4 py-2.5 font-medium whitespace-nowrap", className)}>{children}</th>;
export const Td = ({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) => <td colSpan={colSpan} className={cn("px-4 py-3 align-middle", className)}>{children}</td>;
export function Tr({ children, onClick, className }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return <tr onClick={onClick} className={cn("transition-colors", onClick && "cursor-pointer hover:bg-hover/60", className)}>{children}</tr>;
}

// ── Feedback ──

export function Spinner({ className = "size-5" }: { className?: string }) {
  return <span className={cn("inline-block animate-spin rounded-full border-2 border-current border-r-transparent", className)} role="status" aria-label="Loading" />;
}

export function FullPageSpinner() {
  return <div className="grid min-h-screen place-items-center text-muted"><Spinner className="size-6" /></div>;
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="flex items-center justify-center gap-3 py-16 text-sm text-muted"><Spinner className="size-4" />{label}</div>;
}

export function EmptyState({ icon = "info", title, body, action }: { icon?: IconName; title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 grid size-10 place-items-center rounded-full bg-raised text-muted"><Icon name={icon} className="size-5" /></div>
      <p className="text-sm font-medium">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Use for a failed useApi() load. */
export function LoadError({ error, retry }: { error: { message: string } | null; retry?: () => void }) {
  if (!error) return null;
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm text-bad">{error.message}</p>
      {retry && <Button size="sm" icon="refresh" onClick={retry}>Try again</Button>}
    </div>
  );
}

// ── Navigation helpers ──

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode; count?: number }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((t) => (
        <button key={t.id} type="button" onClick={() => onChange(t.id)}
          className={cn("-mb-px flex items-center gap-2 rounded-t-md border-b-2 px-3 py-2 text-sm whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:bg-white/[0.06] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent/60",
            value === t.id ? "border-accent text-fg [text-shadow:0_0_18px_rgb(255_61_79/0.5)]" : "border-transparent text-muted hover:text-fg")}>
          {t.label}
          {t.count !== undefined && <span className="rounded bg-raised px-1.5 text-[11px] text-muted tabular-nums">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Pagination({ page, pages, total, onChange }: { page: number; pages: number; total?: number; onChange: (p: number) => void }) {
  if (pages <= 1 && total === undefined) return null;
  return (
    <div className="flex items-center justify-between gap-4 border-t border-line px-4 py-3 text-xs text-muted">
      <span>{total !== undefined ? `${total.toLocaleString()} total` : ""}</span>
      <div className="flex items-center gap-2">
        <IconButton icon="chevronLeft" label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)} />
        <span className="tabular-nums">Page {page} of {Math.max(1, pages)}</span>
        <IconButton icon="chevronRight" label="Next page" disabled={page >= pages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search…", className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cn("relative", className)}>
      <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" />
    </div>
  );
}

/** Debounces a fast-changing value (e.g. search input) before it hits the API. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <IconButton icon={done ? "check" : "copy"} label={label}
      onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1200); }} />
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={cn("font-mono text-xs text-muted", className)}>{children}</code>;
}

// ── Dialogs ──

export function Dialog({ open, onClose, title, description, children, footer, wide }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} onCancel={(e) => { e.preventDefault(); onClose(); }}
      className={cn("m-auto w-[calc(100%-2rem)] rounded-[var(--radius-card)] border border-line bg-surface p-0 text-fg shadow-2xl", wide ? "max-w-3xl" : "max-w-md")}>
      {open && (
        <>
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <h2 className="text-base font-semibold">{title}</h2>
              {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
            </div>
            <IconButton icon="x" label="Close" onClick={onClose} />
          </header>
          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
        </>
      )}
    </dialog>
  );
}

/** Slide-over panel from the right, for detail views. */
export function Drawer({ open, onClose, title, children, actions }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-2xl flex-col border-l border-line bg-bg shadow-2xl">
        <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
          <h2 className="min-w-0 truncate text-base font-semibold">{title}</h2>
          <div className="flex items-center gap-2">{actions}<IconButton icon="x" label="Close" onClick={onClose} /></div>
        </header>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  );
}

interface ConfirmOpts { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean }
const ConfirmContext = createContext<(o: ConfirmOpts) => Promise<boolean>>(async () => false);

/** `const confirm = useConfirm(); if (await confirm({ title: "Delete user?", danger: true })) ...` */
export const useConfirm = () => useContext(ConfirmContext);

function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const ask = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Dialog open={!!state} onClose={() => close(false)} title={state?.title ?? ""}
        footer={<>
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button variant={state?.danger ? "danger" : "primary"} onClick={() => close(true)}>{state?.confirmLabel ?? "Confirm"}</Button>
        </>}>
        {state?.body && <div className="text-sm text-muted">{state.body}</div>}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

// ── Toasts ──

type ToastTone = "success" | "error" | "info";
const ToastContext = createContext<(msg: string, tone?: ToastTone) => void>(() => {});

/** `const toast = useToast(); toast("Saved", "success")` */
export const useToast = () => useContext(ToastContext);

function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<{ id: number; msg: string; tone: ToastTone }[]>([]);
  const nextId = useRef(0);
  const push = useCallback((msg: string, tone: ToastTone = "info") => {
    const id = nextId.current++;
    setToasts((t) => [...t.slice(-3), { id, msg, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto flex items-center gap-2.5 rounded-lg border border-line bg-raised px-4 py-3 text-sm shadow-xl">
            <Icon name={t.tone === "success" ? "check" : t.tone === "error" ? "alert" : "info"}
              className={cn("size-4", t.tone === "success" ? "text-ok" : t.tone === "error" ? "text-bad" : "text-info")} />
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Mount once at the root: toasts + confirm dialogs. */
export function UIProvider({ children }: { children: ReactNode }) {
  return <ToastProvider><ConfirmProvider>{children}</ConfirmProvider></ToastProvider>;
}
